import { motion } from "framer-motion";
import { Check } from "lucide-react";

const TierCard = ({ tier, featured }) => (
    <motion.div
        whileHover={{ y: -6 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className={`relative p-6 md:p-8 border transition-colors duration-300 ${
            featured
                ? "bg-navy text-cream border-navy"
                : "bg-sand border-navy/15 hover:border-coral"
        }`}
        data-testid={`tier-${tier.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
        {featured && (
            <span className="absolute -top-3 left-6 bg-coral text-navy font-mono text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1">
                Más elegido
            </span>
        )}
        <p className={`font-mono text-xs uppercase tracking-[0.2em] mb-3 ${featured ? "text-cream/60" : "text-slateblue"}`}>
            {tier.label}
        </p>
        <p className="font-display text-4xl md:text-5xl font-bold tracking-tight">{tier.price}</p>
        {tier.sub && (
            <p className={`mt-2 font-mono text-xs ${featured ? "text-cream/60" : "text-slateblue"}`}>{tier.sub}</p>
        )}
        {tier.meta && (
            <p className={`mt-3 text-xs ${featured ? "text-coral" : "text-coralDark"}`}>{tier.meta}</p>
        )}
        {tier.features && (
            <ul className={`mt-5 space-y-2 text-sm ${featured ? "text-cream/80" : "text-navy/80"}`}>
                {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                        <Check size={15} className="mt-0.5 shrink-0 text-coral" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
        )}
    </motion.div>
);

export const ServiceChapter = ({ service }) => {
    const groups = service.groups || [{ tiers: service.tiers }];
    const wide = service.tiers?.some((t) => t.features?.length > 4);

    return (
        <motion.article
            id={service.id}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="py-16 md:py-24 border-t border-line first:border-t-0"
            data-testid={`service-${service.id}`}
        >
            <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-10 mb-10">
                <span aria-hidden="true" className="font-display text-7xl md:text-8xl font-black text-stroke-coral leading-none select-none">
                    {service.num}
                </span>
                <div>
                    <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{service.name}</h3>
                    <p className="text-slateblue mt-1 text-base md:text-lg">{service.tagline}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                        {service.meta.map((m) => (
                            <span key={m} className="font-mono text-[11px] uppercase tracking-[0.15em] border border-navy/25 px-3 py-1 text-slateblue">
                                {m}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {groups.map((g) => (
                <div key={g.name || "main"} className="mb-8 last:mb-0">
                    {g.name && (
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-slateblue mb-4">
                            {g.name} <span className="text-coral normal-case tracking-normal">— {g.note}</span>
                        </p>
                    )}
                    <div
                        className={`grid gap-4 md:gap-6 ${
                            wide
                                ? "grid-cols-1 md:grid-cols-3"
                                : "grid-cols-2 lg:grid-cols-4"
                        } ${g.tiers.length === 3 ? "md:grid-cols-3" : ""}`}
                    >
                        {g.tiers.map((t) => (
                            <TierCard key={t.label} tier={t} featured={t.featured} />
                        ))}
                    </div>
                </div>
            ))}

            {service.extras.length > 0 && (
                <p className="mt-6 font-mono text-xs uppercase tracking-[0.15em] text-slateblue">
                    <span className="text-coral font-semibold">Extras:</span> {service.extras.join("  ·  ")}
                </p>
            )}
        </motion.article>
    );
};
